import { PROTECTED_NODE_BYPASS_META_KEY } from '@/components/tiptap-editor/tiptap-extension/protected-node-extension/protected-node-plugin';
import { TABLE_MOVE_ROW_COLUMN_META_KEY } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { snapshotComparePluginKey } from '@tiptap-pro/extension-snapshot-compare';
import { isHistoryTransaction } from '@tiptap/pm/history';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';

export const customDocumentContentPluginKey = new PluginKey('customDocumentContent');

type CustomDocumentType = 'lab' | 'test_question' | 'itlab';

type CustomDocumentRule = {
    /** Node type names that identify this document's primary top-level content (e.g. lab nodes or testQuestion). */
    allowedTopLevelNodeTypes: ReadonlySet<string>;

    /** When true, every top-level child must be in allowedTopLevelNodeTypes (empty doc is allowed). */
    strictTopLevelOnly: boolean;

    /** When true, top-level child count/types/order are frozen once an allowed node exists. */
    lockTopLevelStructure: boolean;

    /** When true, transactions that remove a top-level allowed node are blocked (full-doc replace still allowed). */
    blockAllowedNodeDeletion: boolean;
};

export const CUSTOM_DOCUMENT_RULES: Record<CustomDocumentType, CustomDocumentRule> = {
    lab: {
        allowedTopLevelNodeTypes: new Set(['zyStudio', 'zyClassicLab']),
        strictTopLevelOnly: false,
        lockTopLevelStructure: true,
        blockAllowedNodeDeletion: true,
    },
    test_question: {
        allowedTopLevelNodeTypes: new Set(['testQuestion', 'zyVersionBlock']),
        strictTopLevelOnly: true,
        lockTopLevelStructure: false,
        blockAllowedNodeDeletion: false,
    },
    itlab: {
        allowedTopLevelNodeTypes: new Set(['zyITLab']),
        strictTopLevelOnly: true,
        lockTopLevelStructure: true,
        blockAllowedNodeDeletion: true,
    },
};

export function getCustomDocumentType(doc: PMNode): CustomDocumentType | null {
    const documentType = doc.attrs.documentType;
    if (documentType === 'lab' || documentType === 'test_question' || documentType === 'itlab') {
        return documentType;
    }
    return null;
}

export function isCustomDocument(doc: PMNode): boolean {
    return getCustomDocumentType(doc) !== null;
}

export function hasTopLevelAllowedNode(doc: PMNode, allowedTypes: ReadonlySet<string>): boolean {
    for (let i = 0; i < doc.childCount; i++) {
        if (allowedTypes.has(doc.child(i).type.name)) {
            return true;
        }
    }
    return false;
}

export function topLevelOnlyAllowedTypes(doc: PMNode, allowedTypes: ReadonlySet<string>): boolean {
    for (let i = 0; i < doc.childCount; i++) {
        if (!allowedTypes.has(doc.child(i).type.name)) {
            return false;
        }
    }
    return true;
}

export function topLevelAllowedNodeTypesPreserved(before: PMNode, after: PMNode, allowedTypes: ReadonlySet<string>): boolean {
    for (let i = 0; i < before.childCount; i++) {
        if (!allowedTypes.has(before.child(i).type.name)) {
            continue;
        }
        if (i >= after.childCount || !allowedTypes.has(after.child(i).type.name)) {
            return false;
        }
    }
    return true;
}

export function topLevelStructureMatches(before: PMNode, after: PMNode): boolean {
    if (before.childCount !== after.childCount) {
        return false;
    }
    for (let i = 0; i < before.childCount; i++) {
        if (before.child(i).type !== after.child(i).type) {
            return false;
        }
    }
    return true;
}

function isFullDocumentContentReplacement(tr: Transaction, before: PMNode): boolean {
    if (!tr.getMeta(PROTECTED_NODE_BYPASS_META_KEY)) {
        return false;
    }
    return tr.steps.some(step => step instanceof ReplaceStep && step.from === 0 && step.to === before.content.size);
}

function shouldBypassTransaction(tr: Transaction): boolean {
    return (
        !!tr.getMeta('y-sync$') ||
        !!tr.getMeta('__uniqueIDTransaction') ||
        !!tr.getMeta('zyversion') ||
        tr.getMeta('uiEvent') === 'drop' ||
        !!tr.getMeta(TABLE_MOVE_ROW_COLUMN_META_KEY) ||
        isHistoryTransaction(tr) ||
        !!tr.getMeta(PROTECTED_NODE_BYPASS_META_KEY) ||
        tr.getMeta(snapshotComparePluginKey) !== undefined
    );
}

function shouldBypassStrictTopLevelOnly(tr: Transaction, state: EditorState): boolean {
    return (
        !!tr.getMeta('y-sync$') ||
        isHistoryTransaction(tr) ||
        isFullDocumentContentReplacement(tr, state.doc) ||
        tr.getMeta(snapshotComparePluginKey) !== undefined
    );
}

function isDocumentTypeAttributeOnlyChange(tr: Transaction, state: EditorState): boolean {
    return tr.doc.content.eq(state.doc.content) && getCustomDocumentType(state.doc) !== getCustomDocumentType(tr.doc);
}

function blocksTopLevelAllowedNodeDeletion(tr: Transaction, state: EditorState, rules: CustomDocumentRule): boolean {
    if (!rules.blockAllowedNodeDeletion) {
        return false;
    }
    const allowedTypes = rules.allowedTopLevelNodeTypes;
    if (!hasTopLevelAllowedNode(state.doc, allowedTypes)) {
        return false;
    }
    if (topLevelAllowedNodeTypesPreserved(state.doc, tr.doc, allowedTypes)) {
        return false;
    }
    if (isHistoryTransaction(tr) || !!tr.getMeta('y-sync$') || tr.getMeta(snapshotComparePluginKey) !== undefined) {
        return false;
    }
    return !isFullDocumentContentReplacement(tr, state.doc);
}

function filterCustomDocumentTransaction(tr: Transaction, state: EditorState, docType: CustomDocumentType): boolean {
    const rules = CUSTOM_DOCUMENT_RULES[docType];

    if (blocksTopLevelAllowedNodeDeletion(tr, state, rules)) {
        return false;
    }

    if (rules.strictTopLevelOnly) {
        if (shouldBypassStrictTopLevelOnly(tr, state)) {
            return true;
        }
        // After is dirty (e.g. TrailingNode paragraph). Allow only structure-preserving
        // edits so pre-existing disallowed top-level nodes do not freeze the document.
        if (!topLevelOnlyAllowedTypes(tr.doc, rules.allowedTopLevelNodeTypes)) {
            return topLevelStructureMatches(state.doc, tr.doc);
        }
        // After is clean — fall through to lockTopLevelStructure when configured.
    }

    if (shouldBypassTransaction(tr)) {
        return true;
    }

    if (!rules.lockTopLevelStructure) {
        return true;
    }

    if (!hasTopLevelAllowedNode(state.doc, rules.allowedTopLevelNodeTypes)) {
        return true;
    }

    return topLevelStructureMatches(state.doc, tr.doc);
}

export function createCustomDocumentContentPlugin(): Plugin {
    return new Plugin({
        key: customDocumentContentPluginKey,

        filterTransaction(tr: Transaction, state: EditorState) {
            if (!tr.docChanged) {
                return true;
            }

            if (isDocumentTypeAttributeOnlyChange(tr, state)) {
                return true;
            }

            const stateDocType = getCustomDocumentType(state.doc);
            const trDocType = getCustomDocumentType(tr.doc);
            if (!stateDocType && !trDocType) {
                return true;
            }

            const docType = stateDocType ?? trDocType;
            if (!docType) {
                return true;
            }

            return filterCustomDocumentTransaction(tr, state, docType);
        },
    });
}
